import { CHART_CSS } from './chart-css.js';

/**
 * Virtual Stylesheet
 *
 * Static layout/legend/tooltip/responsive CSS comes from chart-css.js.
 * Dynamic palette and instance rules are generated by convertconfig().
 */
class Stylesheet {
  constructor() {
    // Static CSS — replaces the old addJSONStyles(Layout, Legend, Tooltip, Responsive) pipeline
    this.sheet = CHART_CSS;
    this.rules = [];
  }

  /**
   * Add a CSS rule (used by Legend for per-series checkbox colors)
   * @param {string} selector - A css selector
   * @param {object} rules - An object containing css key/value styles.
   */
  addRule(selector, rules) {
    let ruleStr = '';
    Object.keys(rules).forEach((prop) => {
      ruleStr += `${prop}: ${rules[prop]};`;
    });
    this.sheet += `.svc-${selector}{${ruleStr}}`;
  }

  /**
   * Appends raw css styles to the stylesheet
   * @param {string} str
   */
  addRaw(str) {
    this.sheet += str;
  }

  /**
   * Create CSS rules from the config object's style and instances.
   * Generates per-series palette rules and breakpoint hide rules.
   * @param {object} styles - The merged config object
   * @param {number} plotLength - Number of series/instances
   * @param {string} scopeId - Chart instance scope ID
   * @returns {string} CSS rules string
   */
  convertconfig(styles, plotLength, scopeId) {
    const breakpoints = {
      width: {},
      height: {},
    };
    const rules = parseStyles(styles);
    const breakpointRules = parseBreakpoints(breakpoints);
    return rules + breakpointRules;

    function parseBreakpoints(breakpoints) {
      let ruleset = '';
      Object.keys(breakpoints).forEach((direction) => {
        Object.keys(breakpoints[direction]).forEach((point) => {
          const classnames = breakpoints[direction][point];
          let rule = `@media(max-${direction}: ${point}px){`;
          const names = classnames.map((classname) => {
            return `.svc-${classname}.svc-${classname}`;
          });
          rule += names.join(',');
          rule += '{ display: none; }';
          rule += '}';
          ruleset += rule;
        });
      });
      return ruleset;
    }

    function parseStyles(obj, _key = '', rules = '', path = []) {
      for (const prop in obj) {
        if (prop === 'style') {
          let ruleset = '';
          for (const attr in obj[prop]) {
            if (Object.hasOwn(obj[prop], attr)) {
              ruleset += `${attr}:${obj[prop][attr]};`;
            }
          }

          if (path.length === 0) {
            rules += `figure[data-chart-id] {${ruleset}}`;
          } else {
            const classname = path.reduce((a, b) => {
              return a += ((a === '') ? '' : '-') + b;
            }, '');
            rules += `.svc-${classname}{${ruleset}}`;
          }
        } else if (prop === 'instances') {
          const instances = obj[prop];
          for (let i = 0; i < plotLength; i++) {
            const instance = instances[i];
            if (!instance) continue;
            let ruleset = '';
            for (const attr in instance) {
              if (Object.hasOwn(instance, attr)) {
                ruleset += `${attr}:${instance[attr]};`;
              }
            }
            if (ruleset !== '') {
              const classname = path.reduce((a, b) => {
                return a += ((a === '') ? '' : '-') + b;
              }, '');
              const scope = scopeId
                ? `[data-chart-id="${scopeId}"] `
                : '';
              rules += `${scope}.svc-${classname}-${i} {${ruleset}}`;
            }
          }
        } else if (prop === 'breakpoint') {
          const classname = path.reduce((a, b) => {
            return a += ((a === '') ? '' : '-') + b;
          }, '');
          if (breakpoints.width[obj.breakpoint.width]) {
            breakpoints.width[obj.breakpoint.width].push(classname);
          } else {
            breakpoints.width[obj.breakpoint.width] = [classname];
          }
          if (breakpoints.height[obj.breakpoint.height]) {
            breakpoints.height[obj.breakpoint.height].push(classname);
          } else {
            breakpoints.height[obj.breakpoint.height] = [classname];
          }
        } else if (typeof obj[prop] === 'object') {
          path.push(prop);
          rules = parseStyles(obj[prop], prop, rules, path);
          path.pop();
        }
      }
      return rules;
    }
  }
}

export { Stylesheet };
